import React, { useState, useEffect, useCallback } from "react";
import Avatar from "../../components/ui/Avatar";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import Badge from "../../components/ui/Badge";
import CreateRequestModal from "../../components/modals/CreateRequestModal";
import Toast from "../../components/ui/Toast";
import useAuth from "../../hooks/useAuth";
import useToast from "../../hooks/useToast";
import { isAdmin } from "../../utils/permissions";
import requestService from "../../api/requestService";
import {
  Plus,
  RefreshCw,
  Check,
  X,
  Calendar,
  Briefcase,
  Edit,
  Trash2,
} from "lucide-react";
import ConfirmModal from "../../components/modals/ConfirmModal";

// ─── Pure helpers – defined once outside the component so they are never re-created ─────
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
  const dateStr = date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return `${dayName} - ${dateStr}`;
};

const getStatusVariant = (status) => {
  const variants = {
    pending: "warning",
    approved: "success",
    rejected: "danger",
    edit_requested: "info",
  };
  return variants[status] || "default";
};

const getStatusLabel = (status) => {
  const labels = {
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    edit_requested: "Edit Requested",
  };
  return labels[status] || status;
};

const getRequestTypeLabel = (type) => {
  const labels = {
    vacation: "Vacation",
    mission: "Mission",
    late_coming: "Late Coming",
    early_leave: "Early Leave",
  };
  return labels[type] || type;
};

// ─── RequestCard – mobile view (memoized, only re-renders when its own props change) ────
const RequestCard = React.memo(
  ({
    request,
    isAdminUser,
    employee,
    isActioning,
    onApprove,
    onReject,
    onEdit,
    onDeleteConfirm,
  }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-primary-card rounded-xl border border-gray-100 dark:border-dark-200 shadow-sm overflow-hidden"
    >
      <div className="p-4">
        {isAdminUser && (
          <div className="flex items-center gap-2 mb-3">
            <Avatar src={request.employee?.photo} name={request.employee?.fullNameEn} size="sm" className="!w-9 !h-9" />
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-light-100">
                {request.employee?.fullNameEn || "Unknown"}
              </p>
              <p className="text-xs text-gray-500 dark:text-light-500">
                {request.employee?.employeeCode || "N/A"}
              </p>
            </div>
          </div>
        )}
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-light-100 flex items-center gap-1.5">
              <Briefcase className="w-3.5 h-3.5 text-primary-accent" />
              {getRequestTypeLabel(request.requestType)}
              {request.requestType === "mission" && request.isWFH && (
                <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                  WFH
                </span>
              )}
            </p>
            <p className="text-xs text-gray-500 dark:text-light-500 mt-0.5">
              Submitted {formatDate(request.createdAt)}
            </p>
          </div>
          <Badge variant={getStatusVariant(request.status)}>
            {getStatusLabel(request.status)}
          </Badge>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-xs text-gray-500 dark:text-light-500 mb-0.5">
              Start Date
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-light-100">
              {formatDate(request.startDate)}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-500 dark:text-light-500 mb-0.5">
              End Date
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-light-100">
              {formatDate(request.endDate)}
            </p>
          </div>
        </div>
      </div>
      <div className="px-4 py-2.5 bg-gray-50 dark:bg-dark-400 border-t border-gray-100 dark:border-dark-200 flex items-center gap-2">
        {(isAdminUser || request.employee?._id === employee?._id) &&
          !request.isReplaced &&
          request.status !== "rejected" && (
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => onEdit(request)}
              disabled={isActioning}
              className="flex-1 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center gap-1"
            >
              <Edit className="w-3.5 h-3.5" /> Edit
            </motion.button>
          )}
        {isAdminUser &&
          (request.status === "pending" ||
            request.status === "edit_requested") &&
          !request.createdByAdmin && (
            <>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => onApprove(request._id)}
                disabled={isActioning}
                className="flex-1 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center gap-1"
              >
                <Check className="w-3.5 h-3.5" /> Approve
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => onReject(request._id)}
                disabled={isActioning}
                className="flex-1 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg flex items-center justify-center gap-1"
              >
                <X className="w-3.5 h-3.5" /> Reject
              </motion.button>
            </>
          )}
        {(isAdminUser ||
          (String(request.employee?._id || request.employee) ===
            String(employee?._id || employee?.id) &&
            request.status === "pending")) && (
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => onDeleteConfirm(request._id)}
            disabled={isActioning}
            className="p-1.5 text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg flex items-center justify-center"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </motion.button>
        )}
      </div>
    </motion.div>
  ),
);

// ─── RequestRow – desktop table row (memoized) ───────────────────────────────────────────
const RequestRow = React.memo(
  ({
    request,
    index,
    isAdminUser,
    employee,
    isActioning,
    onApprove,
    onReject,
    onEdit,
    onDeleteConfirm,
  }) => (
    <motion.tr
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="hover:bg-gray-50 dark:hover:bg-dark-400 transition-colors"
    >
      {isAdminUser && (
        <td className="px-6 py-4">
          <div className="flex items-center gap-3">
            <Avatar src={request.employee?.photo} name={request.employee?.fullNameEn} size="sm" />
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-light-100">
                {request.employee?.fullNameEn || "Unknown"}
              </p>
              <p className="text-xs text-gray-500 dark:text-light-500">
                {request.employee?.employeeCode || "N/A"}
              </p>
            </div>
          </div>
        </td>
      )}
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-primary-accent" />
          <div>
            <span className="text-sm font-medium text-gray-900 dark:text-light-100">
              {getRequestTypeLabel(request.requestType)}
            </span>
            {request.requestType === "mission" && request.isWFH && (
              <span className="ml-2 text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                WFH
              </span>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 text-sm text-gray-700 dark:text-light-200">
        {formatDate(request.startDate)}
      </td>
      <td className="px-6 py-4 text-sm text-gray-700 dark:text-light-200">
        {formatDate(request.endDate)}
      </td>
      <td className="px-6 py-4">
        <Badge variant={getStatusVariant(request.status)}>
          {getStatusLabel(request.status)}
        </Badge>
      </td>
      <td className="px-6 py-4 text-sm text-gray-500 dark:text-light-500">
        {formatDate(request.createdAt)}
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-col items-center justify-center gap-1">
          <div className="flex items-center justify-center gap-2">
            {(isAdminUser || request.employee?._id === employee?._id) &&
              !request.isReplaced &&
              request.status !== "rejected" && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onEdit(request)}
                  disabled={isActioning}
                  className="p-2.5 text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Edit"
                >
                  <Edit className="w-4 h-4" />
                </motion.button>
              )}
            {isAdminUser &&
              (request.status === "pending" ||
                request.status === "edit_requested") &&
              !request.createdByAdmin && (
                <>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onApprove(request._id)}
                    disabled={isActioning}
                    className="p-2.5 text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Approve"
                  >
                    {isActioning ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => onReject(request._id)}
                    disabled={isActioning}
                    className="p-2.5 text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Reject"
                  >
                    {isActioning ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                  </motion.button>
                </>
              )}
            {(isAdminUser ||
              (String(request.employee?._id || request.employee) ===
                String(employee?._id || employee?.id) &&
                request.status === "pending")) && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => onDeleteConfirm(request._id)}
                disabled={isActioning}
                className="p-2.5 text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </motion.button>
            )}
          </div>
          {((isAdminUser &&
            request.status !== "pending" &&
            request.status !== "edit_requested") ||
            request.createdByAdmin) && (
            <span className="text-xs text-gray-500 dark:text-light-500">
              {request.createdByAdmin
                ? "Auto-Approved"
                : getStatusLabel(request.status)}
            </span>
          )}
        </div>
      </td>
    </motion.tr>
  ),
);

// How many requests we pull per infinite-scroll page
const PAGE_SIZE = 20;

// ─── Main component ──────────────────────────────────────────────────────────────────────
const Requests = () => {
  const { employee } = useAuth();
  const { toast, showSuccess, showError, hideToast } = useToast();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actioningId, setActioningId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // "New request" from other pages (e.g. Dashboard) opens the create modal.
  useEffect(() => {
    if (!location.state?.openAddModal) return;
    setIsCreateModalOpen(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const isAdminUser = isAdmin(employee);

  // Page we have already loaded; kept in a ref so loadMore never needs to be
  // re-created (and the observer never needs to be re-attached) when it moves.
  const pageRef = React.useRef(0);
  // Guards against firing two "load next page" requests for the same page when
  // the sentinel is intersected repeatedly before the first request resolves.
  const loadingMoreRef = React.useRef(false);

  const fetchPage = useCallback(async (page) => {
    const response = await requestService.getRequests({
      page,
      limit: PAGE_SIZE,
    });
    return {
      list: response.data || [],
      hasMore: Boolean(response.pagination?.hasMore),
    };
  }, []);

  // ── Reset to the first page (mount, refresh, create, real-time update) ──
  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const { list, hasMore: more } = await fetchPage(1);
      pageRef.current = 1;
      setRequests(list);
      setHasMore(more);
    } catch (error) {
      console.error("Failed to fetch requests:", error);
      showError(error.response?.data?.message || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  }, [fetchPage, showError]);

  // ── Append the next page (infinite scroll) ──
  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const nextPage = pageRef.current + 1;
      const { list, hasMore: more } = await fetchPage(nextPage);
      pageRef.current = nextPage;
      setRequests((prev) => {
        // Rows created while scrolling can shift pages – drop any duplicates
        const seen = new Set(prev.map((r) => r._id));
        return [...prev, ...list.filter((r) => !seen.has(r._id))];
      });
      setHasMore(more);
    } catch (error) {
      console.error("Failed to load more requests:", error);
      showError(error.response?.data?.message || "Failed to load more requests");
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMore, fetchPage, showError]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Use a ref so the real-time listener always calls the latest version of fetchRequests
  const fetchRequestsRef = React.useRef(fetchRequests);
  fetchRequestsRef.current = fetchRequests;
  useEffect(() => {
    const handler = () => fetchRequestsRef.current?.();
    window.addEventListener("request-notification", handler);
    return () => window.removeEventListener("request-notification", handler);
  }, []);

  // ── Sentinel observer: loads the next page as it comes into view ──
  const sentinelRef = React.useRef(null);
  const loadMoreRef = React.useRef(loadMore);
  loadMoreRef.current = loadMore;
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreRef.current();
      },
      { rootMargin: "300px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  // ── Approve: update only the affected row locally, no full re-fetch ──
  const handleApprove = useCallback(
    async (requestId) => {
      try {
        setActioningId(requestId);
        await requestService.approveRequest(requestId);
        showSuccess("Request approved successfully");
        setRequests((prev) =>
          prev.map((r) =>
            r._id === requestId ? { ...r, status: "approved" } : r,
          ),
        );
      } catch (error) {
        console.error("Failed to approve request:", error);
        showError(error.response?.data?.message || "Failed to approve request");
      } finally {
        setActioningId(null);
      }
    },
    [showSuccess, showError],
  );

  // ── Reject: same pattern as approve ──
  const handleReject = useCallback(
    async (requestId) => {
      try {
        setActioningId(requestId);
        await requestService.rejectRequest(requestId);
        showSuccess("Request rejected successfully");
        setRequests((prev) =>
          prev.map((r) =>
            r._id === requestId ? { ...r, status: "rejected" } : r,
          ),
        );
      } catch (error) {
        console.error("Failed to reject request:", error);
        showError(error.response?.data?.message || "Failed to reject request");
      } finally {
        setActioningId(null);
      }
    },
    [showSuccess, showError],
  );

  // ── Delete: filter out the removed row locally ──
  const handleDeleteRequest = useCallback(
    async (requestId) => {
      try {
        setActioningId(requestId);
        await requestService.deleteRequest(requestId);
        showSuccess("Request deleted successfully");
        setDeleteConfirmId(null);
        setRequests((prev) => prev.filter((r) => r._id !== requestId));
      } catch (error) {
        console.error("Failed to delete request:", error);
        showError(error.response?.data?.message || "Failed to delete request");
      } finally {
        setActioningId(null);
      }
    },
    [showSuccess, showError],
  );

  // ── Create: re-fetch so the new row includes all populated server fields ──
  const handleCreateRequest = useCallback(
    async (formData) => {
      try {
        setIsSubmitting(true);
        await requestService.createRequest(formData);
        showSuccess("Request created successfully");
        setIsCreateModalOpen(false);
        await fetchRequestsRef.current();
      } catch (error) {
        console.error("Failed to create request:", error);
        showError(error.response?.data?.message || "Failed to create request");
      } finally {
        setIsSubmitting(false);
      }
    },
    [showSuccess, showError],
  );

  // ── Open edit modal: stable reference, no deps needed ──
  const handleEditRequest = useCallback((request) => {
    setEditingRequest(request);
    setIsEditModalOpen(true);
  }, []);

  // ── Edit submit: merge changed fields into the row locally ──
  const handleEditSubmit = useCallback(
    async (formData) => {
      if (!editingRequest) return;
      try {
        setIsSubmitting(true);
        const response = await requestService.editRequest(
          editingRequest._id,
          formData,
        );
        showSuccess(response.message || "Request updated successfully");
        setIsEditModalOpen(false);
        setEditingRequest(null);
        setRequests((prev) =>
          prev.map((r) =>
            r._id === editingRequest._id
              ? {
                  ...r,
                  requestType: formData.requestType,
                  isWFH: formData.isWFH,
                  startDate: formData.startDate,
                  endDate: formData.endDate,
                  // Server sets edit_requested when editing an already-approved request
                  status: r.status === "approved" ? "edit_requested" : r.status,
                }
              : r,
          ),
        );
      } catch (error) {
        console.error("Failed to edit request:", error);
        showError(error.response?.data?.message || "Failed to update request");
      } finally {
        setIsSubmitting(false);
      }
    },
    [editingRequest, showSuccess, showError],
  );

  // Stable callbacks for delete confirmation modal
  const handleDeleteConfirm = useCallback((id) => setDeleteConfirmId(id), []);
  const handleCloseDeleteConfirm = useCallback(
    () => setDeleteConfirmId(null),
    [],
  );

  return (
    <>
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />

      <PageWrapper
        title="Requests"
        subtitle={
          isAdminUser
            ? "Manage all employee requests"
            : "View and manage your requests"
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={fetchRequests}
              disabled={loading}
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <Button size="sm" onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="w-4 h-4" />
              <span className="text-sm font-medium">Create Request</span>
            </Button>
          </div>
        }
      >
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-primary-accent animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-600 dark:text-light-400">
                Loading requests...
              </p>
            </div>
          </div>
        ) : requests.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Calendar className="w-16 h-16 text-gray-400 dark:text-light-500 mx-auto mb-4" />
              <p className="text-lg font-semibold text-gray-700 dark:text-light-300 mb-1">
                No requests found
              </p>
              <p className="text-sm text-gray-500 dark:text-light-500 mb-4">
                {isAdminUser
                  ? "No employee requests yet"
                  : "You haven't created any requests yet"}
              </p>
              <Button onClick={() => setIsCreateModalOpen(true)} size="sm">
                <Plus className="w-4 h-4" />
                Create Your First Request
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden space-y-3">
              {requests.map((request) => (
                <RequestCard
                  key={request._id}
                  request={request}
                  isAdminUser={isAdminUser}
                  employee={employee}
                  isActioning={actioningId === request._id}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onEdit={handleEditRequest}
                  onDeleteConfirm={handleDeleteConfirm}
                />
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="overflow-x-auto rounded-xl shadow-lg">
              <table className="min-w-full">
                <thead className="bg-gradient-to-r from-gray-100 to-gray-50 dark:from-dark-400 dark:to-dark-300">
                  <tr>
                    {isAdminUser && (
                      <th className="px-6 py-5 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                        Employee
                      </th>
                    )}
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Type
                    </th>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Start Date
                    </th>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      End Date
                    </th>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Status
                    </th>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Submitted
                    </th>
                    <th className="px-6 py-5 text-center text-xs font-bold text-gray-800 dark:text-light-200 uppercase tracking-wider border-b-2 border-primary-accent/20">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-primary-card divide-y divide-gray-200 dark:divide-dark-100">
                  {requests.map((request, index) => (
                    <RequestRow
                      key={request._id}
                      request={request}
                      index={index}
                      isAdminUser={isAdminUser}
                      employee={employee}
                      isActioning={actioningId === request._id}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onEdit={handleEditRequest}
                      onDeleteConfirm={handleDeleteConfirm}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Infinite scroll sentinel */}
            {hasMore && (
              <div ref={sentinelRef} className="py-6 flex justify-center">
                {loadingMore ? (
                  <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-light-400">
                    <RefreshCw className="w-4 h-4 text-primary-accent animate-spin" />
                    Loading more requests...
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={loadMore}
                    className="text-sm font-medium text-primary-accent hover:underline"
                  >
                    Load more
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </PageWrapper>

      {/* Create Request Modal */}
      <CreateRequestModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateRequest}
        isSubmitting={isSubmitting}
      />

      {/* Edit Request Modal */}
      <CreateRequestModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingRequest(null);
        }}
        onSubmit={handleEditSubmit}
        isSubmitting={isSubmitting}
        initialData={
          editingRequest
            ? {
                requestType: editingRequest.requestType,
                isWFH: Boolean(editingRequest.isWFH),
                startDate: editingRequest.startDate,
                endDate: editingRequest.endDate,
                employeeId: editingRequest.employee?._id || "",
              }
            : null
        }
        title={
          editingRequest?.status === "approved"
            ? "Request Edit (Requires Admin Approval)"
            : "Edit Request"
        }
      />

      {/* Delete confirmation */}
      <ConfirmModal
        isOpen={Boolean(deleteConfirmId)}
        onClose={handleCloseDeleteConfirm}
        onConfirm={() =>
          deleteConfirmId && handleDeleteRequest(deleteConfirmId)
        }
        title="Delete Request"
        message="Are you sure you want to delete this request? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
};

export default Requests;
